import i18n from "@/i18n";
import { read_country } from "./country-utils";
import { clientFetch, url } from "./fetch-client/fetch_client";
import isPlainObject from "lodash/isPlainObject";

const t = i18n.t;

const dataUnitBase = 1024;
/** biome-ignore-start assist/source/useSortedKeys: Data-units should be in order of size, instead of alphabetical  */
const dataUnitFormats = {
	KB: dataUnitBase,
	MB: dataUnitBase ** 2,
	GB: dataUnitBase ** 3,
	TB: dataUnitBase ** 4,
	PB: dataUnitBase ** 5,
};
/** biome-ignore-end assist/source/useSortedKeys: Data-units should be in order of size, instead of alphabetical  */
const dataUnitArray = Object.keys(dataUnitFormats);

// Note that NextJS uses the term 'search params' instead of (the more proper imo) 'query params' term
// However, for consistency sake I use search param term here
export const search_param_add = (newKey, newValue, currentSearchParams, skipSanatize = false) => {
	const params = new URLSearchParams(currentSearchParams.toString());

	// Not sure it .set encode value correctly therefore use 'encodeURIComponent'
	if (skipSanatize) {
		params.set(newKey, newValue);
	} else {
		params.set(newKey, encodeURIComponent(newValue));
	}

	return params;
};

export const get_address_auto_complete = async (zipCode, houseNumber, houseNumberAppendix) => {
	const baseUrl = "https://api.pdok.nl/bzk/locatieserver/search/v3_1/free";

	// Add the query params, duplicate params are allowed
	const params = [
		["fq", `postcode:${zipCode}`],
		["fq", `huisnummer:${houseNumber}`],
	];
	if (houseNumberAppendix) {
		params.push(["fq", `huisletter:${houseNumberAppendix}`]);
	}
	const { status, body } = await clientFetch(url`${baseUrl}`, {
		params: params,
	}).get();

	return { body: body, status: status };
};

export const parse_bool = (v) => {
	const listTrue = ["true", true, 1];

	if (listTrue.includes(v)) {
		return true;
	}

	return false;
};

export const is_same_day = (date1, date2) => {
	return (
		date1.getFullYear() === date2.getFullYear() &&
		date1.getMonth() === date2.getMonth() &&
		date1.getDate() === date2.getDate()
	);
};

export const add_time = (
	date,
	{ years = 0, months = 0, weeks = 0, days = 0, hours = 0, minutes = 0 } = {},
) => {
	const result = new Date(date);
	const timeUnits = [
		["full_year", years],
		["month", months],
		["date", days + weeks * 7],
		["hours", hours],
		["minutes", minutes],
	];

	for (const [unit, value] of timeUnits) {
		if (value) {
			result[`set${capitalize(unit)}`](result[`get${capitalize(unit)}`]() + value);
		}
	}

	return result;
};

function capitalize(string) {
	return string.charAt(0).toUpperCase() + string.slice(1);
}

export function round_to_decimal_place(num, decimal = 1) {
	const factor = 10 ** decimal;
	return Math.round(num * factor) / factor;
}

/**
 * Converts bytes to a specified format
 * @param {number} bytesNum - The number of bytes to convert
 * @param {('KB'|'MB'|'GB'|'TB'|'PB')} targetFormat - The target format to convert to (kilobytes, megabytes, gigabytes, or terabytes)
 * @param {number} decimal - Number of decimal places to round to (Default: 1)
 * @returns {number} The converted size in the target format
 */
export function bytes_to_format(bytesNum, targetFormat = "MB", decimal = 1) {
	const factor = dataUnitFormats[targetFormat.toUpperCase()];

	return round_to_decimal_place(bytesNum / factor, decimal);
}

/**
 * Converts a size in the specified format to bytes
 * @param {number} size - The size value to convert
 * @param {('KB'|'MB'|'GB'|'TB'|'PB')} sourceFormat - The source format of the size (kilobytes, megabytes, gigabytes, or terabytes)
 * @returns {number} The size converted to bytes
 */
export function format_to_bytes(size, sourceFormat = "MB") {
	const factor = dataUnitFormats[sourceFormat.toUpperCase()];

	return size * factor;
}

/**
 * Convert bytes to a human readable storage string while automatically determining the data unit.
 *
 * This function converts a bytes value to a human readable storage string by first determining the data unit.
 * By using a logarithm with base 1024 and flooring the result, an index is calculated which is used to determine the data unit.
 * The data unit is then used to format the bytes into the desired format, finally being returned in a human readable storage string
 * @param {number} bytesNum - The number of bytes to convert
 * @param {number} decimal - maximum number of decimals to round to (Default: 1)
 * @returns {string} A string containing the size and data unit in the format `{size} {dataUnit}` (e.g. 45 TB, 1.5 GB, etc.). If the conversion wasn't successful, a string is returned informing that the conversion wasn't successful
 */
export function bytesToStorageString(bytesNum, decimal = 1) {
	if (!Number.isFinite(bytesNum)) {
		return t("shared.utils.storage_string.conversion_failed");
	}

	// Determine the index based on the bytes number
	const index = Math.floor(Math.log(bytesNum) / Math.log(1024));

	// Prevent the index from going out of bounds of the list (between 0 and max length of data unit list)
	// This results the data unit to never be below KB and use the largest data unit for indexes above the list length
	const limitedIndex = Math.min(Math.max(index - 1, 0), dataUnitArray.length - 1);
	// Get the data unit from the list
	const dataUnit = dataUnitArray[limitedIndex];

	// Calculate the storage value by formatting the bytes using the data unit
	const storageValue = bytes_to_format(bytesNum, dataUnit, decimal);

	return `${storageValue} ${dataUnit}`;
}

export const status_enum = {
	EXPIRED: "expired",
	FORBIDDEN: "forbidden",
	INVALID: "invalid",
	NOT_ALLOWED: "not_allowed",
	SUCCESS: "success",
	UNKNOWN: "unknown",
};

export const sanitize_formik_key = (key) => key.replace(".", "_");

export const format_amount_handler = (amount) => {
	// Catch values which are null or undefined (achieved by using `== null`, because this statement also catches undefined values unlike `=== null`)
	if (amount == null) {
		return "NaN";
	}

	// Check if amount is rounded
	if (Number.isInteger(amount)) {
		return `€ ${amount},-`;
	}

	return `€ ${amount.toFixed(2).toString().replace(".", ",")}`;
};

export async function determ_is_sepa_member(countryCode) {
	const country = await read_country(countryCode);
	return country.is_sepa_member;
}

export function determ_is_dutch(countryCode) {
	return countryCode ? countryCode.toUpperCase() === "NL" : false;
}

/**
 * Checks if a value is an empty object
 */
export function isEmptyObject(value) {
	return isPlainObject(value) && !value;
}

/**
 * Checks if a value is an empty array
 */
export function isEmptyArray(value) {
	return Array.isArray(value) && !value;
}

/**
 * Checks if a string is an empty string after sanitation
 *
 * This function doesn't direclty check for an empty string, but does this check after a sanitization step to
 * remove unwanted characters or sequences like whitespaces or the words 'undefined' or 'null'.
 *
 * NOTE: This function only works correctly for values of the primitive string type. For support for values of the type `String`, this function should be expanded in the future.
 */
export function isBlankString(value) {
	const sanitizeStringRegex = /\s|undefined|null/g;

	return typeof value === "string" && value.replaceAll(sanitizeStringRegex, "") === "";
}
